import axios from 'axios';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { API_BASE_URL, API_ENDPOINTS } from '../configs/api.config';
import { RoleEnum } from '../types/customer.types';

const useAuthentication = () => {
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDisable, setIsDisable] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Email password signUp function
  const emailSignup = (userData, navigate) => {
    setIsDisable(true);
    const loading = toast.loading('Creating User... Please wait!!!');

    const registerData = {
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      password: userData.password,
      confirmPassword: userData.password,
      phone: userData.phone,
      address: userData.address,
      brandName: userData.brandName || undefined,
      webSite: userData.webSite || undefined,
      role: RoleEnum.CUSTOMER, // Always set role as CUSTOMER for client-side registrations
    };

    axios
      .post(`${API_BASE_URL}${API_ENDPOINTS.AUTH.REGISTER}`, registerData)
      .then((res) => {
        toast.dismiss(loading);
        toast.success('Account created successfully! Please login...');
        // After successful registration, redirect to login
        navigate('/login');
      })
      .catch((error) => {
        toast.dismiss(loading);
        toast.error(error.response?.data?.message || error.message);
      })
      .finally(() => {
        setIsDisable(false);
        setIsLoading(false);
      });
  };

  // Seller registration function
  const sellerSignup = (sellerData, resetForm) => {
    setIsDisable(true);
    const loading = toast.loading('Submitting your application... Please wait!!!');

    const registerData = {
      email: sellerData.email,
      firstName: sellerData.firstName,
      lastName: sellerData.lastName,
      password: sellerData.password,
      confirmPassword: sellerData.password,
      phone: sellerData.phone,
      brandName: sellerData.brandName,
      address: sellerData.address,
      role: RoleEnum.SELLER,
    };

    axios
      .post(`${API_BASE_URL}${API_ENDPOINTS.AUTH.REGISTER}`, registerData)
      .then((res) => {
        toast.dismiss(loading);
        toast.success('Application submitted! Check your email for instructions to access your seller dashboard.');
        if (resetForm) resetForm();
      })
      .catch((error) => {
        toast.dismiss(loading);
        toast.error(error.response?.data?.message || error.message);
      })
      .finally(() => {
        setIsDisable(false);
        setIsLoading(false);
      });
  };

  // Email password signIn function
  const emailSignIn = (email, password, navigate, location, e) => {
    setIsDisable(true);
    const loading = toast.loading('Finding Account... Please wait!!!');

    axios
      .post(`${API_BASE_URL}${API_ENDPOINTS.AUTH.LOGIN}`, { email, password })
      .then((res) => {
        const { token, email: userEmail, role } = res.data;

        // Store token and basic info
        localStorage.setItem('kacha_bazar_token', token);
        localStorage.setItem('kacha_bazar_email', userEmail);
        localStorage.setItem('kacha_bazar_role', role);

        // Fetch full user data
        return axios.get(`${API_BASE_URL}${API_ENDPOINTS.AUTH.ME}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      })
      .then((userRes) => {
        const user = userRes.data;
        setLoggedInUser(user);
        localStorage.setItem('kacha_bazar_user', JSON.stringify(user));

        // Check if user is admin
        setIsAdmin(user.role === RoleEnum.ADMIN);

        toast.dismiss(loading);
        toast.success('Logged in successfully...');
        e.target.reset();

        // For SELLER users, redirect directly to dashboard
        if (user.role === RoleEnum.SELLER) {
          const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname.includes('4001');
          const baseURL = isDevelopment
            ? 'https://dashboard-stage.ziyadha.com/'
            : 'https://dashboard.ziyadha.com/';

          const storedToken = localStorage.getItem('kacha_bazar_token');
          const dashboardURL = `${baseURL}sign-in?email=${encodeURIComponent(user.email)}&token=${encodeURIComponent(storedToken)}&role=${encodeURIComponent(user.role)}`;
          window.location.href = dashboardURL;
        } else {
          const redirect_URI = location.state?.from || '/';
          navigate(redirect_URI);
        }
      })
      .catch((error) => {
        toast.dismiss(loading);
        toast.error(error.response?.data?.message || error.message);
      })
      .finally(() => {
        setIsDisable(false);
        setIsLoading(false);
      });
  };

  // Reset Password Function (Forgot Password)
  const resetPassword = (email, e) => {
    setIsDisable(true);

    axios
      .post(`${API_BASE_URL}${API_ENDPOINTS.AUTH.FORGOT_PASSWORD}`, { email })
      .then(() => {
        toast.success('Check your email inbox. We sent a verification email');
        e.target.reset();
      })
      .catch((err) => toast.error(err.response?.data?.message || err.message))
      .finally(() => {
        setIsDisable(false);
        setIsLoading(false);
      });
  };

  // Confirm Reset Password Function (with token from email)
  const confirmResetPassword = (token, newPassword) => {
    setIsDisable(true);

    return axios
      .post(`${API_BASE_URL}${API_ENDPOINTS.AUTH.RESET_PASSWORD}`, {
        token,
        newPassword,
      })
      .then(() => {
        toast.success('Password reset successfully! Please login with your new password.');
        return true;
      })
      .catch((err) => {
        toast.error(err.response?.data || err.message || 'Failed to reset password');
        throw err;
      })
      .finally(() => {
        setIsDisable(false);
      });
  };

  // Change Password Function (for authenticated users)
  const changePassword = (oldPassword, newPassword) => {
    setIsDisable(true);
    const token = localStorage.getItem('kacha_bazar_token');

    return axios
      .post(
        `${API_BASE_URL}${API_ENDPOINTS.AUTH.CHANGE_PASSWORD}`,
        {
          oldPassword,
          newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then(() => {
        toast.success('Password changed successfully!');
        return true;
      })
      .catch((err) => {
        toast.error(err.response?.data || err.message || 'Failed to change password');
        throw err;
      })
      .finally(() => {
        setIsDisable(false);
      });
  };

  // SignOut function
  const logOut = () => {
    setLoggedInUser(null);
    setIsAdmin(false);
    localStorage.removeItem('kacha_bazar_token');
    localStorage.removeItem('kacha_bazar_user');
    localStorage.removeItem('kacha_bazar_email');
    localStorage.removeItem('kacha_bazar_role');
    toast.error('Logged Out!!!');
    setIsLoading(false);
  };

  // Check if user is already logged in on mount
  useEffect(() => {
    const token = localStorage.getItem('kacha_bazar_token');
    const userStr = localStorage.getItem('kacha_bazar_user');
    const role = localStorage.getItem('kacha_bazar_role');

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        setLoggedInUser(user);
        setIsAdmin(role === RoleEnum.ADMIN);
      } catch (error) {
        // Clear corrupted data
        localStorage.removeItem('kacha_bazar_token');
        localStorage.removeItem('kacha_bazar_user');
        localStorage.removeItem('kacha_bazar_email');
        localStorage.removeItem('kacha_bazar_role');
      }
    }
    setIsLoading(false);
  }, []);

  return {
    isLoading,
    loggedInUser,
    setLoggedInUser,
    isAdmin,
    isDisable,
    emailSignup,
    sellerSignup,
    emailSignIn,
    resetPassword,
    confirmResetPassword,
    changePassword,
    logOut,
  };
};

export default useAuthentication;
